/**
 * Amazon link helpers.
 *
 * Products are stored with a clean, public Amazon URL. A single global
 * affiliate tag (Amazon Associates "Store/Tracking ID") is applied at render
 * time. While the tag is empty every link is a plain public link; the moment a
 * tag is configured in the admin panel, every product link becomes an affiliate
 * link — no per-product editing required.
 */

/**
 * Ensure a pasted URL has a protocol so `new URL()` can parse it.
 * Accepts "www.amazon.com/dp/…" as well as a full "https://…" URL.
 * @param {string} url
 * @returns {string}
 */
export const normalizeUrl = (url) => {
  const trimmed = (url || '').trim();
  if (trimmed === '') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Return the affiliate-tagged version of an Amazon product URL.
 *
 * - Empty/whitespace tag → the URL is returned unchanged (plain public link).
 * - Sets (or replaces) the `tag` query parameter, so the result is idempotent
 *   and correct whether or not the URL already has a query string or tag.
 * - If the URL can't be parsed, it is returned unchanged rather than dropped.
 *
 * @param {string} url - The stored product URL.
 * @param {string} tag - The Amazon Associates tracking ID (may be empty).
 * @returns {string}
 */
export const applyAffiliateTag = (url, tag) => {
  const normalized = normalizeUrl(url);
  if (normalized === '') return '';

  const cleanTag = (tag || '').trim();
  if (cleanTag === '') return normalized;

  try {
    const parsed = new URL(normalized);
    parsed.searchParams.set('tag', cleanTag);
    return parsed.toString();
  } catch {
    return normalized;
  }
};

/**
 * Loose check that a URL points at an Amazon domain (incl. amzn.to short links
 * and regional TLDs). Used only for a soft admin warning — never to block a
 * save, since valid Amazon links take many forms.
 * @param {string} url
 * @returns {boolean}
 */
export const isProbablyAmazonUrl = (url) => {
  const normalized = normalizeUrl(url);
  if (normalized === '') return false;
  try {
    const { hostname } = new URL(normalized);
    return /(^|\.)amazon\.[a-z.]+$/i.test(hostname) || /(^|\.)amzn\.to$/i.test(hostname);
  } catch {
    return false;
  }
};
